import { map, isEmpty, values, first, reduce, chain, debounce } from 'underscore';
import React, { useCallback, useContext, useState, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { withError, withCustomValidation, withValidation, ValidationGroup } from '@bingads-webui-react/validation';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { DsaTarget } from '@bingads-webui/campaign-odata-schemas';
import {
  Label,
  Button,
  Checkbox,
  Input,
} from '@fluentui/react-components';
import {
  MessageBar,
  MessageBarBody,
  MessageBarTitle
} from '@fluentui/react-message-bar';
import { AddRegular } from '@fluentui/react-icons';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { withControlErrorHandling, withUnvalidatedControlErrors } from '@bingads-webui-campaign-react/error-handling';
import { SelectedItem } from '@bingads-webui-campaign-react/pill-item-components';
import {
  DSA_TARGET_TYPES,
  DSA_TARGET_SUB_TYPES,
  DSA_TARGET_OPERAND_TYPE_STRINGS,
  loadCategoryList,
  targetingSourceOptions,
  // loadCategoryNameList,
} from '@bingads-webui-campaign-react/search-campaign-data';
import { HelpPopup } from '@bingads-webui-react/help-popup';
import { HelpLink } from '@bingads-webui-react/help-link';

import { useCurrencyCode } from './utils';
import { DefaultBidDescription } from './dynamic-ad-target-panel/default-bid-description';

import { CategoryTargetPanel, SpecificTargetPanel, CustomLabelTargetPanel } from './dynamic-ad-target-panel';

const ValidatedTextInput = withControlErrorHandling(withUnvalidatedControlErrors(withError(withValidation(Input))));

const getStyles = () =>
  ({
    dynamicAdTargetContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      '& .message-bar': {
        marginTop: '20px',
      },
    },
    dynamicAdTargetOption: {
      paddingLeft: '36px',
      '& .add-button': {
        marginTop: '18px',
      },
    },
    selectedAdTargetTags: {
      '& .tagsContainer': {
        display: 'flex',
        flexWrap: 'wrap',
        gridGap: '8px',
        '& .feeds-number-hint': {
          display: 'flex',
          alignItems: 'center',
        },
      },
      '& .show-more-button': {
        marginTop: '10px',
        marginBottom: '10px',
      },
    },
    targetAllBid: {
      paddingLeft: '36px',
      '& .bid-input-container': {
        display: 'flex',
        gap: '8px',
        alignItems: 'start',
        '& .bid-input': {
          width: '200px',
        },
      },
    },
    targetOptionTitle: {
      display: 'flex',
      alignItems: 'center',
    },
    helpLink: {
      marginLeft: 5,
    },
  });

const AdTargetsPillItem = ({
  title,
  maxNumberWithoutCollapse,
  selectedOptions = [],
  generateItemText,
  onRemoveItem,
  allowEditDynamicAdTarget,
}) => {
  const i18n = useContext(I18nContext);
  const classes = useDefaultStyles(getStyles);

  const selectedOptionsWithErrors = useMemo(() => selectedOptions.filter(item => !isEmpty(item.errors)), [selectedOptions]);
  const selectedOptionsWithoutErrors = useMemo(() => selectedOptions.filter(item => isEmpty(item.errors)), [selectedOptions]);
  const sortedSelectedOptions = useMemo(() => [...selectedOptionsWithErrors, ...selectedOptionsWithoutErrors], [selectedOptionsWithErrors, selectedOptionsWithoutErrors]);

  const [isCollapsed, setIsCollapsed] = useState(false);

  useEffect(() => {
    setIsCollapsed(sortedSelectedOptions.length > maxNumberWithoutCollapse && selectedOptionsWithErrors.length <= maxNumberWithoutCollapse);
  }, [maxNumberWithoutCollapse, sortedSelectedOptions, selectedOptionsWithErrors]);

  return (
    <div className={classnames(classes.selectedAdTargetTags, 'selected-ad-target-tags')}>
      {sortedSelectedOptions.length > 0 && <Label>{`${title} (${sortedSelectedOptions.length})`}</Label>}
      <div className="tagsContainer">
        {
          !isEmpty(sortedSelectedOptions) && map(sortedSelectedOptions, (item, index) => {
            if (isCollapsed && index >= maxNumberWithoutCollapse) {
              return null;
            }
            const itemText = generateItemText(item);
            return (
              <SelectedItem
                key={item.key}
                itemIndex={index}
                itemText={itemText}
                errors={chain(item.errors).map(error => error.message).uniq().value()}
                onDelete={allowEditDynamicAdTarget ? () => onRemoveItem(item) : undefined}
              />
            );
          })
        }
        {
          isCollapsed && (
            <span className="feeds-number-hint">{`+ ${sortedSelectedOptions.length - maxNumberWithoutCollapse}`}</span>
          )
        }
      </div>
      {
        sortedSelectedOptions.length > maxNumberWithoutCollapse && (
          <Button
            className="show-more-button"
            onClick={() => setIsCollapsed(prev => !prev)}
          >
            {isCollapsed ? i18n.getString(_TL_('Show more')) : i18n.getString(_TL_('Show less'))}
          </Button>
      )}
    </div>
  );
};

AdTargetsPillItem.propTypes = {
  title: PropTypes.string.isRequired,
  maxNumberWithoutCollapse: PropTypes.number.isRequired,
  selectedOptions: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    text: PropTypes.string,
  })).isRequired,
  generateItemText: PropTypes.func.isRequired,
  onRemoveItem: PropTypes.func.isRequired,
  allowEditDynamicAdTarget: PropTypes.bool.isRequired,
};

const DynamicAdTargetOption = ({
  selectedTargets,
  generateItemText,
  showAddButton,
  onAddButtonClick,
  allowEditDynamicAdTarget,
}) => {
  const i18n = useContext(I18nContext);
  const classes = useDefaultStyles(getStyles);

  return (
    <div className={classnames(classes.dynamicAdTargetOption, 'dynamic-ad-target-option')}>
      {
        selectedTargets && selectedTargets.length > 0 && map(selectedTargets, selectedTarget => (
          <AdTargetsPillItem
            title={selectedTarget.title}
            maxNumberWithoutCollapse={selectedTarget.maxNumberWithoutCollapse}
            selectedOptions={selectedTarget.items}
            generateItemText={generateItemText}
            onRemoveItem={selectedTarget.onRemoveItem}
            allowEditDynamicAdTarget={allowEditDynamicAdTarget}
          />
        ))
      }
      {
        showAddButton && (
          <Button
            className="add-button"
            size="small"
            icon={<AddRegular />}
            onClick={onAddButtonClick}
            disabled={!allowEditDynamicAdTarget}
          >
            {i18n.getString(_TL_('Add'))}
          </Button>
        )
      }
    </div>
  );
};

DynamicAdTargetOption.propTypes = {
  selectedTargets: PropTypes.arrayOf(PropTypes.shape({
    title: PropTypes.string,
    maxNumberWithoutCollapse: PropTypes.number,
    items: PropTypes.arrayOf(PropTypes.shape({
      key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      text: PropTypes.string,
    })),
    onRemoveItem: PropTypes.func,
  })).isRequired,
  generateItemText: PropTypes.func.isRequired,
  showAddButton: PropTypes.bool.isRequired,
  onAddButtonClick: PropTypes.func.isRequired,
  allowEditDynamicAdTarget: PropTypes.bool.isRequired,
};

const TargetAllBidInput = ({
  i18n, items, onChange, allowEditDynamicAdTarget,
}) => {
  const item = useMemo(() => first(items) || {}, [items]);
  const classes = useDefaultStyles(getStyles);
  const currency = useCurrencyCode();

  const onValidateBid = useCallback((res) => {
    if (items) {
      onChange({
        items: [{ ...item, Bid: res.data.Bid }],
      });
    }
  }, [item, items, onChange]);

  return (
    <div className={classes.targetAllBid}>
      <Label>{i18n.getString(_TL_('Bid amount'))}</Label>
      <div className="bid-input-container">
        <div className="bid-input">
          <ValidatedTextInput
            id="input-bid"
            validationProps={{
              data: item,
              onValidate: onValidateBid,
              field: 'Bid',
              schema: DsaTarget,
            }}
            i18n={i18n}
            disabled={!allowEditDynamicAdTarget}
          />
        </div>
        <Label>{currency}</Label>
      </div>
      <DefaultBidDescription />
    </div>
  );
};

TargetAllBidInput.propTypes = {
  onChange: PropTypes.func.isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  items: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  allowEditDynamicAdTarget: PropTypes.bool.isRequired,
};

const DynamicAdTarget = ({
  validationProps, updateValidation,
}) => {
  const { data: adGroup } = validationProps;
  const {
    websites, DsaTargets, selectedWebsiteLanguage, allowEditDynamicAdTarget, selectedTargetingSource,
  } = adGroup;
  const website = first(websites) || '';
  const websiteWithoutProtocol = website.replace(/(^\w+:|^)\/\//, '');

  const i18n = useContext(I18nContext);
  const classes = useDefaultStyles(getStyles);
  const currency = useCurrencyCode();

  const {
    [DSA_TARGET_TYPES.categoryTargets]: categoryTargets,
    [DSA_TARGET_TYPES.allTargets]: allTargets,
    [DSA_TARGET_TYPES.specificTargets]: specificTargets,
    [DSA_TARGET_TYPES.customLabelTargets]: customLabelTargets,
  } = DsaTargets || {};

  const {
    [DSA_TARGET_SUB_TYPES.exactUrls]: exactUrls,
    [DSA_TARGET_SUB_TYPES.rules]: rules,
  } = specificTargets || {};

  const categoryTitle = useMemo(() => (
    <div className={classes.targetOptionTitle}>
      <span >{i18n.getString(_TL_('Target categories of webpages'))}</span>
      <HelpPopup
        topic="pop_BA_Search_TargetCategories"
        aria-label={i18n.getString(_TL_('Click to learn more about Target categories of webpages'))}
      />
    </div>
  ), [i18n, classes]);

  const allTitle = useMemo(() => (
    <div className={classes.targetOptionTitle}>
      <span >{i18n.getString(_TL_('Target all webpages'))}</span>
      <HelpPopup
        topic="pop_BA_CampaignCreate_Keywords_AllPages"
        aria-label={i18n.getString(_TL_('Click to learn more about Target all webpages'))}
      />
    </div>
  ), [i18n, classes]);

  const specificTitle = useMemo(() => (
    <div className={classes.targetOptionTitle}>
      <span >{i18n.getString(_TL_('Target specific webpages'))}</span>
      <HelpPopup
        topic="pop_BA_Search_SpecificWebpages"
        aria-label={i18n.getString(_TL_('Click to learn more about Target specific webpages'))}
      />
    </div>
  ), [i18n, classes]);

  const customLabelTitle = useMemo(() => (
    <div className={classes.targetOptionTitle}>
      <span >{i18n.getString(_TL_('Target custom labels from your page feed(s)'))}</span>
      <HelpPopup
        topic="pop_BA_Search_CustomLabel"
        aria-label={i18n.getString(_TL_('Click to learn more about Target custom labels from your page feed(s)'))}
      />
    </div>
  ), [i18n, classes]);

  const [isCategoryTargetPanelOpen, setIsCategoryTargetPanelOpen] = useState(false);
  const [isSpecificTargetPanelOpen, setIsSpecificTargetPanelOpen] = useState(false);
  const [isCustomLabelTargetPanelOpen, setIsCustomLabelTargetPanelOpen] = useState(false);

  const validateDynamicTargets = useCallback((dynamicTargets) => {
    const {
      [DSA_TARGET_TYPES.categoryTargets]: newCategoryTargets,
      [DSA_TARGET_TYPES.allTargets]: newAllTargets,
      [DSA_TARGET_TYPES.specificTargets]: newSpecificTargets,
      [DSA_TARGET_TYPES.customLabelTargets]: newCustomLabelTargets,
    } = dynamicTargets;

    let isValid = false;
    if (newCategoryTargets && !newCategoryTargets.disabled && newCategoryTargets.selected && newCategoryTargets.items && newCategoryTargets.items.length > 0) {
      isValid = true;
    }
    if (newAllTargets && !newAllTargets.disabled && newAllTargets.selected) {
      isValid = true;
    }
    if (newSpecificTargets && !newSpecificTargets.disabled && newSpecificTargets.selected && values(newSpecificTargets).some(subValue => subValue.items && subValue.items.length > 0)) {
      isValid = true;
    }
    if (newCustomLabelTargets && !newCustomLabelTargets.disabled && newCustomLabelTargets.selected && newCustomLabelTargets.items && newCustomLabelTargets.items.length > 0) {
      isValid = true;
    }
    return isValid ? [] : [i18n.getString(_TL_('Please select and add at least one target'))];
  }, [i18n]);

  const onOptionChange = option => (_evt, data) => {
    const newDsaTargets = {
      ...DsaTargets,
      [option]: {
        ...DsaTargets[option],
        selected: data.checked,
      },
    };

    const errors = validateDynamicTargets(newDsaTargets);
    updateValidation(errors, { DsaTargets: newDsaTargets });
  };

  const onDsaTargetsChange = option => (newDsaSubTargets) => {
    const newDsaTargets = {
      ...DsaTargets,
      [option]: {
        ...DsaTargets[option],
        ...newDsaSubTargets,
      },
    };

    const errors = validateDynamicTargets(newDsaTargets);
    updateValidation(errors, { DsaTargets: newDsaTargets });
  };

  const onDsaTargetsRemove = option => (item) => {
    const newDsaTargets = {
      ...DsaTargets,
      [option]: {
        ...DsaTargets[option],
        items: DsaTargets[option].items.filter(value => value.key !== item.key),
      },
    };

    const errors = validateDynamicTargets(newDsaTargets);
    updateValidation(errors, { DsaTargets: newDsaTargets });
  };

  const onSubDsaTargetsRemove = subOption => (item) => {
    const newDsaTargets = {
      ...DsaTargets,
      specificTargets: {
        ...specificTargets,
        [subOption]: {
          ...specificTargets[subOption],
          items: specificTargets[subOption].items.filter(value => value.key !== item.key),
        },
      },
    };

    const errors = validateDynamicTargets(newDsaTargets);
    updateValidation(errors, { DsaTargets: newDsaTargets });
  };

  const [preloadedCategories, setPreloadedCategories] = useState([]);
  const renderCycleRef = useRef(0);
  const loadCategory = useCallback((domain, language) => {
    loadCategoryList(domain, language).then((response) => {
      if (window.e2eTestEnvironment) {
        setPreloadedCategories([{ Bid: 0, CategoryName: 'browse/', Coverage: 0.1334 }]);
      } else {
        setPreloadedCategories(response.totalCount ? response : []);
      }
    });
  }, []);
  const debouncedLoadCategory = useMemo(() => debounce(loadCategory, 500), [loadCategory]);
  // const [preloadedCategoryNames, setPreloadedCategoryNames] = useState([]);
  useEffect(() => {
    if (!isEmpty(adGroup)) {
      if (websiteWithoutProtocol) {
        debouncedLoadCategory(websiteWithoutProtocol, selectedWebsiteLanguage);
        // loadCategoryNameList(websiteWithoutProtocol).then((response) => {
        //   setPreloadedCategoryNames(response);
        // });
      } else {
        setPreloadedCategories([]);
        // setPreloadedCategoryNames([]);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [websiteWithoutProtocol, selectedWebsiteLanguage]);

  const onCategoryUpdate = useCallback((data) => {
    if (!isEmpty(adGroup)) {
      const newDsaTargets = {
        ...DsaTargets,
        [DSA_TARGET_TYPES.categoryTargets]: {
          ...DsaTargets[DSA_TARGET_TYPES.categoryTargets],
          disabled: data.disabled,
          // 1. skip updating items in the first 2 render cycles, that's caused by mounting (mount preloadedCategories; mount website, update preloadedCategories); in mounting, don't clear
          // 2. whenever user changes website, clear the selected categories
          items: renderCycleRef.current <= 2 ? DsaTargets[DSA_TARGET_TYPES.categoryTargets].items : [],
          selected: data.disabled ? false : DsaTargets[DSA_TARGET_TYPES.categoryTargets].selected,
        },
      };

      const errors = validateDynamicTargets(newDsaTargets);
      updateValidation(errors, { DsaTargets: newDsaTargets }, false);
    }
  }, [adGroup, DsaTargets, updateValidation, validateDynamicTargets]);

  useEffect(() => {
    renderCycleRef.current += 1;
    if (adGroup && adGroup.DsaTargets) {
      onCategoryUpdate({ disabled: isEmpty(preloadedCategories) });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preloadedCategories]);

  useEffect(() => {
    if (adGroup && adGroup.DsaTargets) {
      const newDsaTargets = {
        ...DsaTargets,
        [DSA_TARGET_TYPES.customLabelTargets]: {
          ...DsaTargets[DSA_TARGET_TYPES.customLabelTargets],
          disabled: selectedTargetingSource === targetingSourceOptions.SystemIndex,
        },
      };

      const errors = validateDynamicTargets(newDsaTargets);
      updateValidation(errors, { DsaTargets: newDsaTargets }, false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTargetingSource]);

  return (
    <ValidationGroup
      validationProps={{
        stopPropagation: true,
      }}
    >
      <div className={classes.dynamicAdTargetContainer}>
        <div className="category-target-section">
          <Label>{`${i18n.getString(_TL_('Your website:'))} ${website}`}</Label>
          <Checkbox
            label={categoryTitle}
            disabled={!allowEditDynamicAdTarget || (categoryTargets && categoryTargets.disabled)}
            checked={!!(categoryTargets && categoryTargets.selected)}
            onChange={onOptionChange(DSA_TARGET_TYPES.categoryTargets)}
          />
          {categoryTargets && categoryTargets.selected && (
            <DynamicAdTargetOption
              selectedTargets={[{
                  title: i18n.getString(_TL_('Categories')),
                  maxNumberWithoutCollapse: 3,
                  items: categoryTargets.items,
                  onRemoveItem: onDsaTargetsRemove(DSA_TARGET_TYPES.categoryTargets),
                }]}
              showAddButton
              onAddButtonClick={() => setIsCategoryTargetPanelOpen(true)}
              generateItemText={(item) => {
                const condition = first(item.conditions) || {};
                return `(${i18n.getCurrencySymbol(currency)}${item.Bid} ${currency}) ${condition.ArgumentOperand}`;
              }}
              allowEditDynamicAdTarget={allowEditDynamicAdTarget}
            />
          )}
        </div>
        <div className="all-target-section">
          <Checkbox
            label={allTitle}
            disabled={!allowEditDynamicAdTarget || (allTargets && allTargets.disabled)}
            checked={!!(allTargets && allTargets.selected)}
            onChange={onOptionChange(DSA_TARGET_TYPES.allTargets)}
          />
          {allTargets && allTargets.selected && (
            <TargetAllBidInput i18n={i18n} items={allTargets.items} onChange={onDsaTargetsChange(DSA_TARGET_TYPES.allTargets)} allowEditDynamicAdTarget={allowEditDynamicAdTarget} />
          )}
        </div>
        <div className="specific-target-section">
          <Checkbox
            label={specificTitle}
            disabled={!allowEditDynamicAdTarget || (specificTargets && specificTargets.disabled)}
            checked={!!(specificTargets && specificTargets.selected)}
            onChange={onOptionChange(DSA_TARGET_TYPES.specificTargets)}
          />
          {specificTargets && specificTargets.selected && (
            <>
              <DynamicAdTargetOption
                selectedTargets={[{
                  title: i18n.getString(_TL_('Exact URLs')),
                  maxNumberWithoutCollapse: 3,
                  items: exactUrls.items,
                  onRemoveItem: onSubDsaTargetsRemove(DSA_TARGET_SUB_TYPES.exactUrls),
                }]}
                generateItemText={(item) => {
                  const condition = first(item.conditions) || {};
                  return `(${i18n.getCurrencySymbol(currency)}${item.Bid} ${currency}) ${condition.ArgumentOperand}`;
                }}
                showAddButton={false}
                onAddButtonClick={() => setIsSpecificTargetPanelOpen(true)}
                allowEditDynamicAdTarget={allowEditDynamicAdTarget}
              />
              <DynamicAdTargetOption
                selectedTargets={[{
                  title: i18n.getString(_TL_('Rules')),
                  maxNumberWithoutCollapse: 3,
                  items: rules.items,
                  onRemoveItem: onSubDsaTargetsRemove(DSA_TARGET_SUB_TYPES.rules),
                }]}
                generateItemText={(item) => {
                  const text = reduce(item.conditions, (acc, condition) => {
                    acc.push(`${i18n.getString(DSA_TARGET_OPERAND_TYPE_STRINGS[condition.Operand])} (${condition.ArgumentOperand})`);
                    return acc;
                  }, []);
                  return `${i18n.getCurrencySymbol(currency)}${item.Bid} ${currency} · ${text.join(' · ')}`;
                }}
                showAddButton
                onAddButtonClick={() => setIsSpecificTargetPanelOpen(true)}
                allowEditDynamicAdTarget={allowEditDynamicAdTarget}
              />
            </>
          )}
        </div>
        <div className="custom-label-target-section">
          <Checkbox
            label={customLabelTitle}
            disabled={!allowEditDynamicAdTarget || (customLabelTargets && customLabelTargets.disabled)}
            checked={!!(customLabelTargets && customLabelTargets.selected)}
            onChange={onOptionChange(DSA_TARGET_TYPES.customLabelTargets)}
          />
          {customLabelTargets && customLabelTargets.selected && (
            <DynamicAdTargetOption
              selectedTargets={[{
                title: i18n.getString(_TL_('Custom labels')),
                maxNumberWithoutCollapse: 3,
                items: customLabelTargets.items,
                onRemoveItem: onDsaTargetsRemove(DSA_TARGET_TYPES.customLabelTargets),
              }]}
              generateItemText={(item) => {
                const condition = first(item.conditions) || {};
                return `(${i18n.getCurrencySymbol(currency)}${item.Bid} ${currency}) ${condition.ArgumentOperand}`;
              }}
              showAddButton
              onAddButtonClick={() => setIsCustomLabelTargetPanelOpen(true)}
              allowEditDynamicAdTarget={allowEditDynamicAdTarget}
            />
          )}
        </div>
        {!allowEditDynamicAdTarget && (
          <MessageBar className="message-bar">
            <MessageBarBody>
              <MessageBarTitle>{i18n.getString(_TL_('Note'))}</MessageBarTitle>
              {/* eslint-disable-next-line react/no-danger */}
              <span dangerouslySetInnerHTML={{ __html: i18n.getString(_TL_('We only show 200 dynamic ad targets. To see all targets and edit them, go to <b>Campaigns > Dynamic ad targets</b>.')) }} />
            </MessageBarBody>
          </MessageBar>
        )}
        <MessageBar className="message-bar">
          <MessageBarBody>
            <MessageBarTitle>{i18n.getString(_TL_('Note'))}</MessageBarTitle>
            {i18n.getString(_TL_("We automatically generate Dynamic Search ads for you based on your website's content. Set up dynamic ad targets to tell us which pages of your website to focus on."))}
            <HelpLink query="help:app54695" className={classes.helpLink}>
              {i18n.getString(_TL_('Learn more'))}
            </HelpLink>
          </MessageBarBody>
        </MessageBar>
      </div>
      <CategoryTargetPanel
        data={categoryTargets}
        onDataChange={onDsaTargetsChange(DSA_TARGET_TYPES.categoryTargets)}
        title={categoryTitle}
        isOpen={isCategoryTargetPanelOpen}
        onClose={() => setIsCategoryTargetPanelOpen(false)}
        domainName={websiteWithoutProtocol}
        preloadedCategories={preloadedCategories}
        languageCode={adGroup && adGroup.selectedWebsiteLanguage}
      />
      <SpecificTargetPanel
        data={specificTargets}
        onDataChange={onDsaTargetsChange(DSA_TARGET_TYPES.specificTargets)}
        title={specificTitle}
        isOpen={isSpecificTargetPanelOpen}
        onClose={() => setIsSpecificTargetPanelOpen(false)}
        preloadedCategories={preloadedCategories}
        domainName={websiteWithoutProtocol}
        languageCode={adGroup && adGroup.selectedWebsiteLanguage}
        selectedTargetingSource={selectedTargetingSource}
      />
      <CustomLabelTargetPanel
        data={customLabelTargets}
        onDataChange={onDsaTargetsChange(DSA_TARGET_TYPES.customLabelTargets)}
        title={customLabelTitle}
        isOpen={isCustomLabelTargetPanelOpen}
        onClose={() => setIsCustomLabelTargetPanelOpen(false)}
      />
    </ValidationGroup>
  );
};

DynamicAdTarget.propTypes = {
  updateValidation: PropTypes.func.isRequired,
  validationProps: PropTypes.shape({
    data: PropTypes.shape({
      websites: PropTypes.arrayOf(PropTypes.string),
      DsaTargets: PropTypes.shape({}),
      allowEditDynamicAdTarget: PropTypes.bool,
      selectedWebsiteLanguage: PropTypes.string,
      selectedTargetingSource: PropTypes.string,
    }),
  }).isRequired,
};

export const ValidatedDynamicAdTarget = withError(withCustomValidation(DynamicAdTarget));

